import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import DocsObligatoiresBanner from "./DocsObligatoiresBanner";
import { 
  getAdminDashboard, 
  getMembreDashboard, 
  getEvents, 
  getKPIs,
  getRevenueMonthlyChart,
  getFoodTrucksStatusChart,
  getPaymentsStatusChart,
  getEventsByMonthChart,
  getMonthlyReportUrl,
  getExportSyntheseFinanciereUrl
} from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import {
  Users,
  Calendar,
  AlertTriangle,
  Euro,
  TrendingUp,
  Clock,
  Truck,
  ArrowRight,
  Briefcase,
  Award,
  Target,
  PieChart as PieChartIcon,
  BarChart3,
  Trophy,
  Download,
  FileText,
  UserPlus,
  Inbox,
  Handshake,
  UserCheck,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const COLORS = ['#8B1538', '#5C3D2E', '#FF9F1C', '#9B59B6', '#2E7D32', '#0288D1'];

export default function DashboardPage() {
  const { user, isBureau } = useAuth();
  const [stats, setStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [membreStats, setMembreStats] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [eventsData, setEventsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const navigate = useNavigate();

  const moisNoms = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
                   "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  useEffect(() => {
    loadDashboard();
  }, [user]);

  const loadDashboard = async () => {
    try {
      const kpisRes = await getKPIs();
      setKpis(kpisRes.data);

      if (isBureau()) {
        const [dashRes, eventsRes, revenueRes, statusRes, paymentsRes, eventsChartRes] = await Promise.all([
          getAdminDashboard(),
          getEvents(),
          getRevenueMonthlyChart(),
          getFoodTrucksStatusChart(),
          getPaymentsStatusChart(),
          getEventsByMonthChart()
        ]);
        setStats(dashRes.data);
        setRevenueData(revenueRes.data);
        setStatusData(statusRes.data);
        setPaymentsData(paymentsRes.data);
        setEventsData(eventsChartRes.data);
        
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = eventsRes.data
          .filter(e => e.date_debut >= today)
          .sort((a, b) => a.date_debut.localeCompare(b.date_debut))
          .slice(0, 5);
        setUpcomingEvents(upcoming);
      } else if (user?.food_truck_id) {
        const [membreRes] = await Promise.all([
          getMembreDashboard(user.food_truck_id),
        ]);
        setMembreStats(membreRes.data);
      }
    } catch (error) {
      console.error("Dashboard error:", error);
      toast.error("Erreur de chargement du tableau de bord");
    } finally {
      setLoading(false);
    }
  };

  const downloadMonthlyReport = () => {
    const token = localStorage.getItem("token");
    const url = getMonthlyReportUrl(selectedYear, selectedMonth);
    
    fetch(url, {
      headers: { "Authorization": `Bearer ${token}` }
    })
    .then(res => res.blob())
    .then(blob => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rapport_mensuel_${selectedYear}_${selectedMonth.toString().padStart(2, '0')}.pdf`;
      link.click();
      toast.success("Rapport téléchargé");
    })
    .catch(() => toast.error("Erreur de téléchargement"));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
    <Card className="bg-white border-[#E5E0DC] hover:shadow-md transition-shadow card-hover">
      <CardContent className="p-3 sm:p-4 lg:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{title}</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-secondary mt-0.5 sm:mt-1">{value}</p>
            {subtitle && <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 truncate">{subtitle}</p>}
          </div>
          <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl ${color} flex-shrink-0`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-[#E5E0DC] rounded-lg shadow-lg">
          <p className="font-semibold text-secondary">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' ? `${entry.value.toFixed(2)} €` : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 px-2 sm:px-0" data-testid="dashboard">
      <DocsObligatoiresBanner />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-primary">
            Bonjour, {user?.nom}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {isBureau() ? "Vue administrateur" : "Votre espace membre"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isBureau() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const token = localStorage.getItem("token");
                window.open(`${getExportSyntheseFinanciereUrl()}?token=${token}`, "_blank");
              }}
              className="border-[#E5E0DC] text-xs sm:text-sm"
              data-testid="export-synthese-btn"
            >
              <Download className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Synthèse Excel</span>
              <span className="sm:hidden">Export</span>
            </Button>
          )}
          <Badge 
            variant="outline" 
            className="capitalize border-primary text-primary px-3 py-1 text-xs sm:text-sm"
          >
            {user?.role}
          </Badge>
        </div>
      </div>

      {/* Admin Dashboard */}
      {isBureau() && stats && kpis && (
        <>
          {/* Main Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            <StatCard
              title="Total Membres"
              value={stats.total_membres}
              icon={Users}
              color="bg-primary/10 text-primary"
              subtitle={`${stats.membres_bureau} bureau, ${stats.membres_standard} standard`}
            />
            <StatCard
              title="Événements"
              value={stats.evenements_a_venir}
              icon={Calendar}
              color="bg-accent/10 text-accent"
              subtitle={`${kpis.evenements_ce_mois} ce mois`}
            />
            <StatCard
              title="Revenus"
              value={`${kpis.total_revenus.toFixed(0)} €`}
              icon={Euro}
              color="bg-status-success/10 text-status-success"
              subtitle={`${kpis.total_impaye.toFixed(0)} € impayés`}
            />
            <StatCard
              title="Alertes"
              value={stats.alertes_impayes}
              icon={AlertTriangle}
              color={stats.alertes_impayes > 0 ? "bg-status-error/10 text-status-error" : "bg-status-success/10 text-status-success"}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Revenue Chart */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Revenus par mois
                </CardTitle>
              </CardHeader>
              <CardContent>
                {revenueData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E0DC" />
                      <XAxis dataKey="mois" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="cotisations" name="Cotisations" fill="#8B1538" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="evenements" name="Événements" fill="#FF9F1C" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="bureau" name="Bureau" fill="#9B59B6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>

            {/* Events Chart */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Événements par mois
                </CardTitle>
              </CardHeader>
              <CardContent>
                {eventsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={eventsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E0DC" />
                      <XAxis dataKey="mois" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="collectifs" name="Collectifs" stroke="#8B1538" fill="#8B1538" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="individuels" name="Individuels" stroke="#FF9F1C" fill="#FF9F1C" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Pie Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Food Trucks Status */}
            {statusData && (
              <Card className="bg-white border-[#E5E0DC]">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-secondary flex items-center gap-2">
                    <Truck className="w-5 h-5 text-primary" />
                    Répartition membres
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={statusData.type}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {statusData.type.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Payments Status */}
            {paymentsData && (
              <Card className="bg-white border-[#E5E0DC]">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-secondary flex items-center gap-2">
                    <Euro className="w-5 h-5 text-primary" />
                    État des paiements
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Cotisations</span>
                      <span className="font-semibold">{paymentsData.cotisations.payes}/{paymentsData.cotisations.payes + paymentsData.cotisations.non_payes}</span>
                    </div>
                    <Progress 
                      value={paymentsData.cotisations.payes + paymentsData.cotisations.non_payes > 0 ? 
                        (paymentsData.cotisations.payes / (paymentsData.cotisations.payes + paymentsData.cotisations.non_payes)) * 100 : 0} 
                      className="h-3"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Événements</span>
                      <span className="font-semibold">{paymentsData.evenements.payes}/{paymentsData.evenements.payes + paymentsData.evenements.non_payes}</span>
                    </div>
                    <Progress 
                      value={paymentsData.evenements.payes + paymentsData.evenements.non_payes > 0 ? 
                        (paymentsData.evenements.payes / (paymentsData.evenements.payes + paymentsData.evenements.non_payes)) * 100 : 0} 
                      className="h-3"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Achats bureau</span>
                      <span className="font-semibold">{paymentsData.bureau.payes}/{paymentsData.bureau.payes + paymentsData.bureau.non_payes}</span>
                    </div>
                    <Progress 
                      value={paymentsData.bureau.payes + paymentsData.bureau.non_payes > 0 ? 
                        (paymentsData.bureau.payes / (paymentsData.bureau.payes + paymentsData.bureau.non_payes)) * 100 : 0} 
                      className="h-3"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Monthly Report Download */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-secondary flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Rapport mensuel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="border-[#E5E0DC]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="border-[#E5E0DC]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {moisNoms.slice(1).map((m, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="w-full bg-primary hover:bg-primary-hover"
                  onClick={downloadMonthlyReport}
                  data-testid="download-report-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Top Apporteurs & Events */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Apporteurs de Deals */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-accent" />
                  Top Apporteurs de Deals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {kpis.top_apporteurs && kpis.top_apporteurs.length > 0 ? (
                  <div className="space-y-3">
                    {kpis.top_apporteurs.map((apporteur, index) => (
                      <div 
                        key={apporteur.food_truck_id}
                        className="flex items-center gap-4 p-3 bg-[#F9F7F5] rounded-lg"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? "bg-accent" : index === 1 ? "bg-secondary" : "bg-muted-foreground"
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-secondary">{apporteur.nom_food_truck}</p>
                          <p className="text-sm text-muted-foreground">
                            {apporteur.total_participants} participants générés
                          </p>
                        </div>
                        <Badge className="bg-accent text-black">
                          <Award className="w-3 h-3 mr-1" />
                          {apporteur.nombre_deals} deals
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Aucun apporteur de deal enregistré
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Prochains événements
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate("/calendar")}
                  className="text-primary hover:text-primary-hover"
                  data-testid="view-calendar-btn"
                >
                  Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {upcomingEvents.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingEvents.map((event) => (
                      <div 
                        key={event.id} 
                        className="flex items-center gap-4 p-3 bg-[#F9F7F5] rounded-lg hover:bg-[#F0EBE6] transition-colors cursor-pointer"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex flex-col items-center justify-center">
                          <span className="text-xs font-medium text-primary uppercase">
                            {format(parseISO(event.date_debut), "MMM", { locale: fr })}
                          </span>
                          <span className="text-lg font-bold text-primary">
                            {format(parseISO(event.date_debut), "dd")}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-secondary truncate">{event.titre}</p>
                          <p className="text-sm text-muted-foreground truncate">{event.lieu}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant={event.type_evenement === "collectif" ? "default" : "secondary"}>
                            {event.type_evenement}
                          </Badge>
                          {event.apporteur_deal_nom && (
                            <span className="text-xs text-accent font-medium flex items-center gap-1">
                              <Award className="w-3 h-3" /> {event.apporteur_deal_nom}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Aucun événement à venir</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* CRM KPIs Section */}
          <Card className="bg-gradient-to-r from-purple-50 to-white border-purple-200">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-purple-700 flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                KPI CRM - Pipeline Commercial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* Nouveaux Leads */}
                <div 
                  className="bg-white border border-purple-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate("/crm")}
                >
                  <div className="flex justify-center mb-2">
                    <div className="p-2 bg-purple-100 rounded-full">
                      <Inbox className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-purple-700">{kpis.crm_leads || 0}</p>
                  <p className="text-xs text-muted-foreground">Nouveaux Leads</p>
                </div>

                {/* Prospects */}
                <div 
                  className="bg-white border border-blue-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate("/crm")}
                >
                  <div className="flex justify-center mb-2">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <Target className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">{kpis.crm_prospects || 0}</p>
                  <p className="text-xs text-muted-foreground">Prospects</p>
                </div>

                {/* En négociation */}
                <div 
                  className="bg-white border border-yellow-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate("/crm")}
                >
                  <div className="flex justify-center mb-2">
                    <div className="p-2 bg-yellow-100 rounded-full">
                      <Handshake className="w-5 h-5 text-yellow-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-yellow-700">{kpis.crm_en_negociation || 0}</p>
                  <p className="text-xs text-muted-foreground">En négociation</p>
                </div>

                {/* Clients actifs */}
                <div 
                  className="bg-white border border-green-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate("/crm")}
                >
                  <div className="flex justify-center mb-2">
                    <div className="p-2 bg-green-100 rounded-full">
                      <UserCheck className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{kpis.crm_clients_actifs || 0}</p>
                  <p className="text-xs text-muted-foreground">Clients actifs</p>
                </div>

                {/* Total clients */}
                <div 
                  className="bg-white border border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate("/crm")}
                >
                  <div className="flex justify-center mb-2">
                    <div className="p-2 bg-gray-100 rounded-full">
                      <Users className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-700">{kpis.crm_total_clients || 0}</p>
                  <p className="text-xs text-muted-foreground">Total clients</p>
                </div>

                {/* Contacts 30j */}
                <div 
                  className="bg-white border border-primary/30 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate("/crm")}
                >
                  <div className="flex justify-center mb-2">
                    <div className="p-2 bg-primary/10 rounded-full">
                      <TrendingUp className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-primary">{kpis.crm_contacts_30j || 0}</p>
                  <p className="text-xs text-muted-foreground">Contacts (30j)</p>
                </div>
              </div>

              {/* Pipeline visual */}
              <div className="mt-6 flex items-center justify-center gap-2">
                <div className="flex-1 h-2 bg-purple-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 transition-all" 
                    style={{ width: kpis.crm_total_clients > 0 ? `${(kpis.crm_leads / kpis.crm_total_clients) * 100}%` : '0%' }}
                  />
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1 h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all" 
                    style={{ width: kpis.crm_total_clients > 0 ? `${(kpis.crm_prospects / kpis.crm_total_clients) * 100}%` : '0%' }}
                  />
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1 h-2 bg-yellow-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 transition-all" 
                    style={{ width: kpis.crm_total_clients > 0 ? `${(kpis.crm_en_negociation / kpis.crm_total_clients) * 100}%` : '0%' }}
                  />
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1 h-2 bg-green-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all" 
                    style={{ width: kpis.crm_total_clients > 0 ? `${(kpis.crm_clients_actifs / kpis.crm_total_clients) * 100}%` : '0%' }}
                  />
                </div>
              </div>
              <div className="mt-2 flex items-center justify-center text-xs text-muted-foreground">
                <span>Pipeline : Leads → Prospects → Négociation → Clients</span>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Member Dashboard */}
      {!isBureau() && (
        <>
          {user?.food_truck_id && membreStats ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Montant total dû"
                  value={`${membreStats.montant_total_du.toFixed(2)} €`}
                  icon={Euro}
                  color={membreStats.montant_total_du > 0 ? "bg-status-warning/10 text-status-warning" : "bg-status-success/10 text-status-success"}
                />
                <StatCard
                  title="Cotisations"
                  value={`${membreStats.cotisations_dues.toFixed(2)} €`}
                  icon={Receipt}
                  color="bg-primary/10 text-primary"
                />
                <StatCard
                  title="Frais événements"
                  value={`${membreStats.frais_evenements_dus.toFixed(2)} €`}
                  icon={Calendar}
                  color="bg-accent/10 text-accent"
                />
                <StatCard
                  title="Frais bureau"
                  value={`${membreStats.frais_bureau_dus.toFixed(2)} €`}
                  icon={Briefcase}
                  color="bg-highlight/10 text-highlight"
                />
              </div>

              <Card className="bg-white border-[#E5E0DC]">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    Mes prochains événements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {membreStats.prochains_evenements.length > 0 ? (
                    <div className="space-y-3">
                      {membreStats.prochains_evenements.map((event, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 bg-[#F9F7F5] rounded-lg">
                          <Clock className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-semibold text-secondary">{event.titre}</p>
                            <p className="text-sm text-muted-foreground">
                              {event.date} - {event.lieu}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">Aucun événement à venir</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-8 text-center">
                <Truck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl text-secondary mb-2">Aucun food truck associé</h3>
                <p className="text-muted-foreground">
                  Contactez un administrateur pour associer votre compte à votre food truck.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

const Receipt = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/>
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
    <path d="M12 17.5v-11"/>
  </svg>
);
